interface Connection extends EventTarget { readonly saveData?: boolean; readonly effectiveType?: string }
const connection = () => (navigator as Navigator & { connection?: Connection }).connection;
const limited = () => connection()?.saveData === true || ['slow-2g', '2g'].includes(connection()?.effectiveType ?? '');
let interactive = false, optedIn = false, paused = false;
let pending: Promise<void> | undefined, finish: (() => void) | undefined;
let timer: ReturnType<typeof setTimeout> | undefined, idle: number | undefined;
const listeners = new Set<() => void>();
const publish = (value: boolean) => { if (paused !== value) { paused = value; for (const listener of listeners) listener(); } };

export const offlineInstallStore = {
  subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
  getSnapshot: () => paused,
};

function cancelScheduled() {
  clearTimeout(timer); timer = undefined;
  if (idle !== undefined) { window.cancelIdleCallback(idle); idle = undefined; }
}
function eligible() { return interactive && navigator.onLine !== false && document.visibilityState !== 'hidden' && (!limited() || optedIn); }
function schedule() {
  if (!finish) return;
  cancelScheduled(); publish(interactive && limited() && !optedIn);
  if (!eligible()) return;
  // A quiet foreground gives typing, navigation and foreground resource loading priority.
  timer = setTimeout(() => {
    const ready = () => {
      idle = undefined;
      if (!eligible()) { schedule(); return; }
      const resolve = finish; finish = undefined; pending = undefined;
      listen(false); publish(false); resolve?.();
    };
    if ('requestIdleCallback' in window) idle = window.requestIdleCallback(ready, { timeout: 10_000 });
    else ready();
  }, 2_000);
}
function listen(add: boolean) {
  const method = add ? 'addEventListener' : 'removeEventListener';
  window[method]('online', schedule);
  document[method]('visibilitychange', schedule);
  document[method]('keydown', schedule, true);
  document[method]('pointerdown', schedule, true);
  connection()?.[method]('change', schedule);
}

/** Called after the actual signed-in/out surface has painted, never by the launch splash. */
export function markShellInteractive() { interactive = true; schedule(); }
/** Explicit user choice permits complete warming even on a restricted connection. */
export function allowOfflineInstall() { optedIn = true; schedule(); }

/** Keep full recursive warming inside worker installation. Deferring registration never turns a
 * partial cache into an activated offline release, and all worker consumers share this gate.
 */
export function waitForOfflineInstall(): Promise<void> {
  if (pending) return pending;
  pending = new Promise<void>(resolve => { finish = resolve; });
  listen(true); schedule();
  return pending;
}
